import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';
import { AdminLogs } from './AdminLogs';
import api from '../services/api';
import { UserEditModal } from '../components/admin/UserEditModal';
import RoleBadge from '../components/common/RoleBadge';
import InviteDialog from '../components/admin/InviteDialog';
import InvitesList from '../components/admin/InvitesList';
import TemplateManager from '../components/admin/TemplateManager';
import UserDetailPanel from '../components/admin/UserDetailPanel';
import ActivityFeed from '../components/dashboards/ActivityFeed';
import NeedsAttention from '../components/dashboards/NeedsAttention';
import { StackHealthPanel } from '../components/health/StackHealthPanel';
import { useAnalytics } from '../hooks/useAnalytics';

interface UserData {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  disabled: boolean;
  is_superuser: boolean;
  created_at: string;
  updated_at: string;
  auth_provider?: string;
}

// Overview data from GET /admin/overview
interface OverviewData {
  entities: Record<string, number>;
  tokens: { today_total: number; today_cost_usd: number; week_total: number; week_cost_usd: number };
  version: string;
  release_notes_count: number;
}

interface AdminDashboardProps {
  activeTab?: string;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ activeTab: initialTab }) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserData[]>([]);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeTab = initialTab || 'overview';
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);

  // Observatory analytics for the cards
  const persona = useAnalytics({ entityType: 'persona' });
  const workflow = useAnalytics({ entityType: 'workflow' });
  const artifactory = useAnalytics({ entityType: 'artifactory' });

  useEffect(() => {
    if (user && !user.is_superuser) navigate('/');
  }, [user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const usersResponse = await api.get('/auth/users');
        setUsers(usersResponse.data);

        try {
          const overviewResp = await api.get('/admin/overview');
          setOverview(overviewResp.data);
        } catch { /* overview fetch is non-critical */ }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load admin data');
      } finally {
        setLoading(false);
      }
    };
    if (user?.is_superuser) fetchData();
  }, [user, refreshTrigger]);

  if (!user || !user.is_superuser) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Access denied.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // Chip styles
  const chipBase = `text-xs px-3 py-1.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap`;
  const chip = `${chipBase} ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`;
  const chipAccent = `${chipBase} ${darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60 hover:text-red-200' : 'bg-red-50 text-red-700 hover:bg-red-100'}`;

  return (
    <div className="max-w-6xl mx-auto">
      {error && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${darkMode ? 'bg-red-900/50 text-red-200' : 'bg-red-50 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* ── Overview — Command Center ── */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Metrics Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[
              { label: 'Users', value: overview?.entities?.users ?? '—', sub: `${overview?.entities?.users_active ?? 0} active` },
              { label: 'Personas', value: overview?.entities?.personas ?? '—' },
              { label: 'Sessions', value: overview?.entities?.sessions ?? '—' },
              { label: 'Artifacts', value: overview?.entities?.artifacts ?? '—' },
              { label: 'Tokens Today', value: overview?.tokens?.today_total?.toLocaleString() ?? '—' },
              { label: 'Cost Today', value: overview?.tokens?.today_cost_usd != null ? `$${overview.tokens.today_cost_usd.toFixed(2)}` : '—' },
              { label: 'Audit (24h)', value: overview?.entities?.audit_entries_24h ?? '—' },
              { label: 'Version', value: overview?.version ?? '—', mono: true },
            ].map((m) => (
              <div key={m.label} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                <div className={`text-[10px] uppercase tracking-wider font-medium mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{m.label}</div>
                <div className={`text-lg font-bold ${m.mono ? (darkMode ? 'font-mono text-red-400 text-sm' : 'font-mono text-red-600 text-sm') : (darkMode ? 'text-white' : 'text-gray-900')}`}>{m.value}</div>
                {m.sub && <div className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{m.sub}</div>}
              </div>
            ))}
          </div>

          {/* Observatory Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: 'Forge Monitor',
                path: '/dashboards/personas',
                stat: persona.summary?.total ?? '...',
                sub: `${persona.summary?.active_30d ?? 0} active last 30d`,
                gradient: darkMode ? 'from-purple-900/60 to-purple-800/40' : 'from-purple-50 to-purple-100',
                accent: darkMode ? 'text-purple-300' : 'text-purple-700',
              },
              {
                title: 'Phase Watch',
                path: '/dashboards/workflows',
                stat: workflow.summary?.total ?? '...',
                sub: `${workflow.summary?.total_executions ?? 0} pipeline runs`,
                gradient: darkMode ? 'from-blue-900/60 to-blue-800/40' : 'from-blue-50 to-blue-100',
                accent: darkMode ? 'text-blue-300' : 'text-blue-700',
              },
              {
                title: 'Blueprint Vault',
                path: '/dashboards/artifactory',
                stat: artifactory.summary?.total ?? '...',
                sub: 'blueprints',
                gradient: darkMode ? 'from-cyan-900/60 to-cyan-800/40' : 'from-cyan-50 to-cyan-100',
                accent: darkMode ? 'text-cyan-300' : 'text-cyan-700',
              },
            ].map((c) => (
              <a
                key={c.title}
                href={c.path}
                target="_blank"
                rel="noopener noreferrer"
                className={`block text-left p-5 rounded-xl bg-gradient-to-br ${c.gradient} transition-all hover:scale-[1.02] hover:shadow-lg group`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${c.accent}`}>{c.title}</span>
                  <svg className={`w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity ${c.accent}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </div>
                <div className={`text-3xl font-bold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{c.stat}</div>
                <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.sub}</div>
              </a>
            ))}
          </div>

          {/* Stack Health */}
          <StackHealthPanel />

          {/* Quick Actions — visually separated from header tabs */}
          <div className="pt-2">
            <div className={`text-[10px] uppercase tracking-wider font-medium mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>Quick actions</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/admin/chat')} className={chipAccent}>Admin Chat</button>
              <button onClick={() => navigate('/admin/invites')} className={chip}>Invite User</button>
              <a href="/personas/new" target="_blank" rel="noopener noreferrer" className={`${chip} inline-flex items-center gap-1`}>
                New Persona
                <svg className="w-2.5 h-2.5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <button onClick={() => navigate('/admin/logs')} className={chip}>Audit Log</button>
            </div>
          </div>

          {/* Live Activity Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              <div className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Recent Activity</div>
              <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                <ActivityFeed />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className={`text-xs font-semibold uppercase tracking-wider mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Needs Attention</div>
              <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
                <NeedsAttention />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Users ── */}
      {activeTab === 'users' && (
        <div className={`rounded-lg shadow-sm overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
              <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  {['User', 'Email', 'Status', 'Role', 'Provider', 'Created', ''].map((h, i) => (
                    <th key={i} scope="col" className={`px-5 py-3 ${i === 6 ? 'text-right' : 'text-left'} text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {users.map((u) => (
                  <tr key={u.id} className={darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {u.full_name
                            ? u.full_name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
                            : (u.username?.substring(0, 2).toUpperCase() || u.email?.substring(0, 2).toUpperCase())}
                        </div>
                        <div>
                          <button
                            onClick={() => setViewingUserId(u.id)}
                            className={`text-sm font-medium text-left hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            {u.full_name || u.username || u.email.split('@')[0]}
                          </button>
                          <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{u.username}</div>
                        </div>
                      </div>
                    </td>
                    <td className={`px-5 py-3 whitespace-nowrap text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{u.email}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`inline-flex text-xs font-medium px-2 py-0.5 rounded-full ${
                        u.disabled
                          ? darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-600'
                          : darkMode ? 'bg-emerald-900/40 text-emerald-300' : 'bg-emerald-50 text-emerald-600'
                      }`}>
                        {u.disabled ? 'Disabled' : 'Active'}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {/* TODO: fetch actual role_name from backend */}
                      <RoleBadge role={u.is_superuser ? 'admin' : 'viewer'} />
                    </td>
                    <td className={`px-5 py-3 whitespace-nowrap text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {u.auth_provider || 'local'}
                    </td>
                    <td className={`px-5 py-3 whitespace-nowrap text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <button
                        className={`text-xs px-2 py-1 rounded mr-2 ${darkMode ? 'text-sky-400 hover:bg-gray-700' : 'text-sky-600 hover:bg-sky-50'}`}
                        onClick={() => setViewingUserId(u.id)}
                      >
                        View
                      </button>
                      <button
                        className={`text-xs px-2 py-1 rounded mr-2 ${darkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
                        onClick={() => setEditingUser(u)}
                      >
                        Edit
                      </button>
                      <button
                        className={`text-xs px-2 py-1 rounded ${
                          u.disabled
                            ? darkMode ? 'text-emerald-400 hover:bg-gray-700' : 'text-emerald-600 hover:bg-emerald-50'
                            : darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'
                        }`}
                        onClick={async () => {
                          try {
                            await api.put(`/auth/users/${u.id}`, { disabled: !u.disabled });
                            showToast(`User ${u.username} ${u.disabled ? 'enabled' : 'disabled'}`, 'success');
                            setRefreshTrigger(prev => prev + 1);
                          } catch (err: any) {
                            showToast(err.response?.data?.detail || 'Failed to toggle user status', 'error');
                          }
                        }}
                      >
                        {u.disabled ? 'Enable' : 'Disable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'logs' && <AdminLogs />}

      {activeTab === 'invites' && user?.is_superuser && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Invites</h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium ${
                darkMode ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60' : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              {showAddForm ? 'Cancel' : '+ Create Invite'}
            </button>
          </div>
          {showAddForm && (
            <InviteDialog
              onClose={() => setShowAddForm(false)}
              onInviteCreated={() => setRefreshTrigger(prev => prev + 1)}
            />
          )}
          <div className={`rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <InvitesList refreshTrigger={refreshTrigger} />
          </div>
        </div>
      )}

      {activeTab === 'templates' && user?.is_superuser && <TemplateManager />}

      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onUserUpdated={() => setRefreshTrigger(prev => prev + 1)}
          darkMode={darkMode}
        />
      )}

      {viewingUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-3xl h-[80vh] overflow-y-auto rounded-lg">
            <UserDetailPanel
              userId={viewingUserId}
              onClose={() => setViewingUserId(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
